import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { doc, onSnapshot, serverTimestamp, updateDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { TeamsBoard } from '../components/TeamsTab';

const BRAND_NAMES = { mst: 'MST Sports Tours', your_tour: 'Your Tour', padelante: 'Padelante' };

const ms = (t) => (t && t.toMillis ? t.toMillis() : 0);

// No-login teams page for coaches, opened from the emailed/copied link.
// Reads and writes only coachLinks/{token}; Cloud Functions mirror the tour's
// teams + player names onto it and copy the coach's changes back to the tour
// (functions/coachLinks.js).
export default function TeamsAssignmentPage() {
  const [params] = useSearchParams();
  const token = params.get('token');

  const [state, setState] = useState('loading');
  const [error, setError] = useState('');
  const [saveError, setSaveError] = useState('');
  const [tourName, setTourName] = useState('');
  const [brand, setBrand] = useState('mst');
  const [teams, setTeams] = useState([]);
  const [assignments, setAssignments] = useState({});
  const [participants, setParticipants] = useState([]);
  const [saving, setSaving] = useState(false);
  const inFlight = useRef(0);
  const loadedRef = useRef(false);

  useEffect(() => {
    if (!token) { setState('error'); setError('No token found in this link.'); return undefined; }
    const unsub = onSnapshot(doc(db, 'coachLinks', token), { includeMetadataChanges: true }, (snap) => {
      if (!snap.exists() || snap.data().revoked || snap.data().kind !== 'teams') {
        setState('error');
        setError('This link is invalid or has been replaced. Please ask MST for a new one.');
        return;
      }
      const link = snap.data();
      if (!link.mirroredAt) { setState('loading'); return; } // trigger still filling it
      setTourName(link.tourName || '');
      setBrand(link.brand || 'mst');
      setParticipants(link.participants || []);
      // Take the server's teams/placements only once our own edits have
      // landed, so a quick second move isn't briefly undone by the first.
      const settled = !snap.metadata.hasPendingWrites && inFlight.current === 0
        && ms(link.syncedCoachAt) >= ms(link.coachUpdatedAt);
      if (settled || !loadedRef.current) {
        setTeams(link.teams || []);
        setAssignments(link.assignments || {});
        loadedRef.current = true;
      }
      setState((link.teams || []).length ? 'ready' : 'empty');
    }, (err) => {
      console.error(err);
      setState('error');
      setError('Failed to load. Please try again.');
    });
    return unsub;
  }, [token]);

  async function handleChange(nextTeams, nextAssignments) {
    setTeams(nextTeams);
    setAssignments(nextAssignments);
    setSaveError('');
    inFlight.current += 1;
    setSaving(true);
    try {
      await updateDoc(doc(db, 'coachLinks', token), {
        assignments: nextAssignments,
        teamNames: Object.fromEntries(nextTeams.map((t) => [t.id, t.name])),
        coachUpdatedAt: serverTimestamp(),
      });
    } catch (err) {
      console.error(err);
      setSaveError('That change could not be saved. Please check your connection and try again.');
    } finally {
      inFlight.current -= 1;
      if (inFlight.current === 0) setSaving(false);
    }
  }

  const header = (
    <div className="bg-[#0D2240] rounded-[12px] p-6 text-center">
      <p className="text-white font-bold text-[18px]">{BRAND_NAMES[brand] || BRAND_NAMES.mst}</p>
      {tourName && <p className="text-[#93C5FD] text-[13px] mt-1">{tourName}</p>}
      <p className="text-[#93C5FD] text-[12px] mt-0.5">Teams</p>
    </div>
  );

  if (state === 'loading') {
    return (
      <div className="min-h-screen bg-[#F9FAFB] flex items-center justify-center">
        <p className="text-[#9CA3AF] text-[14px]">Loading…</p>
      </div>
    );
  }

  if (state === 'error' || state === 'empty') {
    return (
      <div className="min-h-screen bg-[#F9FAFB] py-10 px-4">
        <div className="max-w-md mx-auto space-y-5">
          {state === 'empty' && header}
          <div className="bg-white rounded-[14px] border border-[#E5E7EB] p-8 text-center">
            <p className="text-[15px] font-bold text-[#111827] mb-2">
              {state === 'empty' ? 'Teams not set up yet' : 'Link not valid'}
            </p>
            <p className="text-[13px] text-[#6B7280]">
              {state === 'empty' ? 'MST will create the teams for your tour. This page will update when they do.' : error}
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F9FAFB] py-10 px-4">
      <div className="max-w-3xl mx-auto space-y-5">
        {header}
        <p className="text-[12px] text-center text-[#9CA3AF]">
          {saving ? 'Saving…' : 'All changes saved'}
        </p>
        <TeamsBoard
          teams={teams}
          assignments={assignments}
          participants={participants}
          canManageTeams={false}
          onChange={handleChange}
          error={saveError}
          intro="Rename each team if you like, then tap a player and tap a team to place them. Tap a player already in a team to move them. Changes save automatically, and you can come back to this link at any time."
        />
      </div>
    </div>
  );
}
